<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Check, ChevronDown, ChevronRight, Info } from 'lucide-svelte';

  import { groupCentroidsByIndustry, loadPublicIndustryCentroids } from '$lib/api/data';
  import type { IndustryCentroid } from '$lib/api/data';
  import { industryCentroids, selectedIndustryCentroids } from '$lib/stores/data';
  import { expandedIndustryGroups, industryProfile } from '$lib/stores/industryProfile';

  /**
   * Choosing industries: the categories, collapsed, with a search across them.
   *
   * # One tree, two places
   *
   * This existed twice — once in the guided search screen and once in the
   * data-selection popup — with the same grouping, the same search, the same
   * expand-collapse and the same select-all-in-group written out separately.
   * They had already drifted: different counts, different empty states,
   * different behaviour when a search matched a category name rather than an
   * industry inside it.
   *
   * # It loads its own list
   *
   * `industryCentroids` used to be filled only by whoever happened to mount
   * first. A component that needs the centroids asks for them.
   *
   * # The number on a row is companies
   *
   * `/deep-iq/industry-centroids` carries two precomputed figures per centroid
   * and only one is usable. `number_of_companies` is exact — centroid 1814
   * reports 1,388 and a search returns 1,388. `number_of_contacts` counts every
   * row in materialized_contacts at the assigned companies with no condition on
   * harmonized_role, while the app can only sell a contact whose role is one of
   * the ten the filter offers: 64.7% of contacts carry no harmonised role at
   * all. The same centroid advertises 4,471 contacts where selecting every
   * available role returns 1,784.
   *
   * The popup showed that contact figure beside the company one. A number two
   * and a half times what the customer can actually buy does not belong next to
   * a price, so both views count companies and the real contact figure comes
   * from the running total, which is measured rather than stored.
   */
  let {
    /** The search text. Bindable, so a caller with its own search box can drive it. */
    query = $bindable(''),

    /** Whether to render a search box. False where the surrounding screen has one. */
    showSearch = true,

    /** Start with every category open, which suits a wide popup more than a column. */
    expandAll = false,

    /** Called after any change to the selection. */
    onChange = () => {}
  }: {
    query?: string;
    showSearch?: boolean;
    expandAll?: boolean;
    onChange?: () => void;
  } = $props();

  let loading = $state(false);

  onMount(async () => {
    if ($industryCentroids.length === 0) {
      loading = true;
      try {
        await loadPublicIndustryCentroids();
      } finally {
        loading = false;
      }
    }

    if (expandAll) {
      expandedIndustryGroups.set(
        new Set(groupCentroidsByIndustry($industryCentroids).map((g) => g.id))
      );
    }
  });

  const needle = $derived(query.trim().toLowerCase());

  /** A group's heading, for the centroids the API sends with no industry. */
  const label = (name: string | undefined) => name || $_('guided.industry.uncategorised');

  const groups = $derived.by(() => {
    const all = groupCentroidsByIndustry($industryCentroids);
    if (!needle) return all;

    return all
      .map((group) => {
        // A group whose own name matches keeps all of its industries; that is
        // what somebody typing "Bau" is asking for.
        //
        // `industry_name` is omitempty on the wire, so a centroid can arrive
        // without one and the group name is then undefined. Calling toLowerCase
        // on it would throw inside the search — the box would go dead on the
        // first keystroke, on a control whose whole job is to be searchable.
        if (label(group.name).toLowerCase().includes(needle)) return group;

        return {
          ...group,
          centroids: group.centroids.filter((c) => c.name.toLowerCase().includes(needle))
        };
      })
      .filter((group) => group.centroids.length > 0);
  });

  /**
   * A search opens what it found.
   *
   * Without this the matches sit behind collapsed headers and the box looks
   * broken — which is the same complaint, one layer up.
   */
  const open = (groupId: number) => needle.length > 0 || $expandedIndustryGroups.has(groupId);

  function toggleGroup(groupId: number) {
    expandedIndustryGroups.update((groups) => {
      const next = new Set(groups);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }

  function selectedIn(centroids: IndustryCentroid[]): number {
    return centroids.filter((c) => $selectedIndustryCentroids.has(String(c.id))).length;
  }

  function toggle(id: number, name: string) {
    selectedIndustryCentroids.update((selected) => {
      const next = new Map(selected);
      const key = String(id);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, name);
      }
      return next;
    });
    onChange();
  }

  /** Take or drop a whole category at once. */
  function toggleAll(centroids: IndustryCentroid[]) {
    const complete = selectedIn(centroids) === centroids.length;

    selectedIndustryCentroids.update((selected) => {
      const next = new Map(selected);
      for (const centroid of centroids) {
        const key = String(centroid.id);
        if (complete) {
          next.delete(key);
        } else {
          next.set(key, centroid.name);
        }
      }
      return next;
    });
    onChange();
  }

  const swiss = (value: number | undefined) => (value ?? 0).toLocaleString('de-CH');
</script>

<div class="space-y-4">
  {#if showSearch}
    <input
      class="w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
      type="search"
      placeholder={$_('guided.industry.filterPlaceholder')}
      bind:value={query}
    />
  {/if}

  {#if loading}
    <p class="py-8 text-center text-sm text-gray-500">{$_('status.loading')}</p>
  {:else if groups.length === 0}
    <p class="py-8 text-center text-sm text-gray-500">
      {#if needle}
        {$_('guided.industry.nothingMatches', { values: { query } })}
      {:else}
        {$_('guided.industry.noneAvailable')}
      {/if}
    </p>
  {/if}

  <div class="divide-y divide-hairline overflow-y-auto rounded-control border border-hairline">
    {#each groups as group (group.id)}
      {@const chosen = selectedIn(group.centroids)}
      {@const complete = chosen === group.centroids.length}

      <section>
        <!--
          No fill on this row.

          It was `bg-surface-sunken/60`, and collapsed, these header rows are
          the *only* thing the picker shows — so the whole component was a
          stack of grey bands carrying grey chevrons, grey counts and a grey
          action. Four greys and a wash, which is the entire reason it read as
          drab rather than dense.

          The rows are separated by the container's dividers instead, and the
          colour moved to the two things that are actually controls: the
          chevron that opens the group and the button that takes all of it.
          Expanded rows are bordered cards in a grid, so they stay distinct
          from these without a band to tell them apart.
        -->
        <div class="flex items-center gap-2 px-2 py-2">
          <button
            type="button"
            onclick={() => toggleGroup(group.id)}
            class="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left hover:bg-gray-100"
            aria-expanded={open(group.id)}
          >
            {#if open(group.id)}
              <ChevronDown class="h-4 w-4 shrink-0 text-accent-500" />
            {:else}
              <ChevronRight class="h-4 w-4 shrink-0 text-accent-500" />
            {/if}

            <!--
              The full name on hover, because the visible one may not be it.
              These are taxonomy labels — "Beratung in Unternehmensführung und
              Organisation" — and the column they sit in is narrow enough that
              two groups can truncate to the same few words. Choosing between
              them then means guessing.
            -->
            <span
              class="min-w-0 truncate text-sm font-semibold text-ink-800"
              title={label(group.name)}
            >
              {label(group.name)}
            </span>

            {#if chosen > 0}
              <span
                class="shrink-0 rounded-full bg-accent-100 px-1.5 py-0.5 text-2xs font-semibold text-accent-700"
              >
                {chosen}
              </span>
            {/if}

            <span
              class="ml-auto shrink-0 whitespace-nowrap text-xs tabular-nums text-ink-500"
              title={$_('guided.industry.companiesInIndustry')}
            >
              {swiss(group.totalCompanies)}
            </span>
          </button>

          <button
            type="button"
            onclick={() => toggleAll(group.centroids)}
            class="shrink-0 rounded-control px-2 py-1 text-xs font-medium text-accent-600 transition-colors hover:bg-accent-50 hover:text-accent-700"
          >
            {complete ? $_('guided.industry.noneOfThese') : $_('guided.industry.allOfThese')}
          </button>
        </div>

        {#if open(group.id)}
          <!--
            Two columns at most, never three.

            The third only appeared at `xl`, and it bought density at the cost
            of the one thing these rows carry: the name. Each row is a
            checkbox, a taxonomy label and a count, and the labels are long —
            "Beratung in Unternehmensführung und Organisation". Splitting the
            width three ways truncated most of them, so the widest windows were
            the ones that showed the least.

            `grid-cols-1` is written out rather than left implicit. A bare
            `grid` already stacks, so this changes nothing today — it says the
            single column is the intended base case instead of a side effect,
            which is what stops someone adding a base `grid-cols-2` later and
            quietly taking mobile with it.
          -->
          <div class="grid grid-cols-1 gap-1.5 px-2 py-2 sm:grid-cols-2">
            {#each group.centroids as centroid (centroid.id)}
              {@const selected = $selectedIndustryCentroids.has(String(centroid.id))}

              <!--
                A row, not a button.

                It was one button doing the whole width, and the profile needed
                a second control inside it. A button nested in a button is
                invalid, and browsers resolve it by dropping one — so the
                container became a div and the two jobs became two siblings:
                the wide one selects, the narrow one explains.
              -->
              <div
                class="flex items-center rounded-control border transition-colors {selected
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-hairline bg-surface-panel hover:border-accent-300 hover:bg-gray-50'}"
              >
                <button
                  type="button"
                  onclick={() => toggle(centroid.id, centroid.name)}
                  class="flex min-w-0 flex-1 items-center gap-2 py-2 pl-2.5 text-left"
                  aria-pressed={selected}
                >
                  <span
                    class="flex h-4 w-4 shrink-0 items-center justify-center rounded border {selected
                      ? 'border-accent-500 bg-accent-500 text-white'
                      : 'border-gray-300'}"
                  >
                    {#if selected}<Check class="h-3 w-3" />{/if}
                  </span>

                  <!-- Still truncates: two to a row is wider than three,
                       not wide enough for the longest of these. The title
                       attribute is what makes them readable. -->
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900" title={centroid.name}>
                    {centroid.name}
                  </span>

                  <span
                    class="shrink-0 text-xs tabular-nums text-gray-500"
                    title={$_('guided.industry.companiesInIndustry')}
                  >
                    {swiss(centroid.company_count)}
                  </span>
                </button>

                <button
                  type="button"
                  onclick={() => industryProfile.set(centroid)}
                  class="shrink-0 rounded-control px-2 py-2 text-gray-400 transition hover:text-accent-600"
                  title={$_('guided.industry.about')}
                  aria-label={$_('guided.industry.aboutNamed', {
                    values: { name: centroid.name }
                  })}
                >
                  <Info class="h-4 w-4" />
                </button>
              </div>
            {/each}
          </div>
        {/if}
      </section>
    {/each}
  </div>
</div>
